import { useMemo } from 'react'
import InsightViewOption from 'enums/InsightViewOption'
import { useApplicationSettings } from 'hooks/usePageConfig'
import {
  generateSentimentsStrategy,
  generateGendersStrategy,
  generateIncludedTermsStrategy,
  generateIncludedEmojisStrategy,
  generateSentimentsWithTermsStrategy
} from 'utils/insightModal/strategy/widgetSettingComposing'
import { SocialWebStoryScopeWidget as SocialWebStoryScopeWidgetType } from 'schemas/SocialWebStoryScopeWidgetSchema'
import LevelOneType from 'enums/LevelOneType'
import { SelectedInsight } from '../types'

const useWidgetSettingComposingStrategies = (
  widgetSetting: SocialWebStoryScopeWidgetType,
  selectedInsights: SelectedInsight[]
) => {
  const applicationSettings = useApplicationSettings()
  const { levelOneType, viewOption } = widgetSetting

  const levelOneWidgetSettingComposingStrategy = useMemo(() => {
    switch (levelOneType) {
      case LevelOneType.SENTIMENT:
      case LevelOneType.SENTIMENT_WO_NEUTRAL:
        return generateSentimentsStrategy(applicationSettings)
      case LevelOneType.GENDER:
      case LevelOneType.GENDER_WO_UNKNOWN:
        return generateGendersStrategy()
      case LevelOneType.NONE:
        return undefined
      default:
        throw new Error(`Unsupported level one type: ${levelOneType}`)
    }
  }, [levelOneType, applicationSettings])

  const levelTwoWidgetSettingComposingStrategy = useMemo(() => {
    switch (viewOption) {
      case InsightViewOption.TERMS:
      case InsightViewOption.HASHTAGS:
      case InsightViewOption.BRANDS:
      case InsightViewOption.PEOPLE:
        return generateIncludedTermsStrategy(applicationSettings)
      case InsightViewOption.EMOJIS_GROUP:
      case InsightViewOption.EMOJIS_EMOTION:
      case InsightViewOption.EMOJIS_EMOJI:
        return generateIncludedEmojisStrategy()
      case InsightViewOption.THINGS:
        return generateSentimentsWithTermsStrategy(applicationSettings)
      default:
        throw new Error(`Unsupported view option: ${viewOption}`)
    }
  }, [viewOption, applicationSettings])

  const isLevelTwoSelectedInsight = selectedInsights[0]?.levelOneFilter !== undefined
  return [
    ...(levelOneWidgetSettingComposingStrategy ? [levelOneWidgetSettingComposingStrategy] : []),
    ...(isLevelTwoSelectedInsight ? [levelTwoWidgetSettingComposingStrategy] : [])
  ]
}

export default useWidgetSettingComposingStrategies
